import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY } from "@/data/feniks";

const NAV_LINKS = [
  { id: "o-nas", label: "O nas" },
  { id: "uslugi", label: "Usługi" },
  { id: "cennik", label: "Cennik" },
  { id: "obszar", label: "Obszar" },
  { id: "kontakt", label: "Kontakt" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-zinc-200" : "bg-white/70 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("hero")}
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={COMPANY.logo.iconDark}
            alt="Feniks Gastro & AGD logo"
            className="w-9 h-9 md:w-10 md:h-10 object-contain"
          />
          <div className="flex flex-col leading-none">
            <span className="font-display text-base md:text-lg tracking-tight text-zinc-950">
              FENIKS<span className="text-[var(--feniks-red)]"> </span>GASTRO
              <span className="text-[var(--feniks-red)]"> &amp; </span>AGD
            </span>
            <span className="hidden sm:inline text-[9px] tracking-[0.18em] uppercase text-zinc-500 mt-1">
              Serwis · Śląsk &amp; Zagłębie
            </span>
          </div>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-sm font-medium text-zinc-700 hover:text-[var(--feniks-red)] transition-colors uppercase tracking-wider"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phone}`}
            data-testid="navbar-call-btn"
            className="flex items-center gap-2 text-sm font-semibold text-zinc-900 hover:text-[var(--feniks-red)] transition-colors"
          >
            <Phone size={16} /> {COMPANY.phone}
          </a>
          <button
            data-testid="navbar-cta-btn"
            onClick={() => scrollTo("kontakt")}
            className="bg-[var(--feniks-red)] text-white px-5 py-3 text-xs font-bold uppercase tracking-widest hover:bg-[var(--feniks-red-hover)] transition-colors"
          >
            Umów serwis
          </button>
        </div>

        <button
          data-testid="navbar-burger"
          className="md:hidden p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-white border-t border-zinc-200">
          <div className="px-5 py-4 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="text-left text-base font-medium text-zinc-800 uppercase tracking-wider"
              >
                {l.label}
              </button>
            ))}
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="flex items-center gap-2 text-zinc-900 font-semibold pt-3 border-t border-zinc-200"
            >
              <Phone size={16} /> {COMPANY.phone}
            </a>
            <button
              data-testid="mobile-cta-btn"
              onClick={() => scrollTo("kontakt")}
              className="bg-[var(--feniks-red)] text-white py-3 text-xs font-bold uppercase tracking-widest"
            >
              Umów serwis
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
